// GraphViewService.js
// Service for saving / loading graph (dashboard) views — the chart-based counterpart
// of the tabular saved views handled in requestServices.js.

import axios from "../axios-interceptor.js";

const BACKEND_API = process.env.REACT_APP_BACKEND_API;

const authHeaders = (options = {}) => ({
	userId: options.userId || localStorage.getItem("userId") || localStorage.getItem("id") || "1",
	tenantId: options.tenantId || localStorage.getItem("tenantId") || 1,
	...(options.headers || {}),
});

/**
 * Create or update a graph view (dashboard) of charts for a space.
 * POST /graph-view   (new)  |  PUT /graph-view  (existing — graphViewId in body)
 * @param {object} payload - { name, spaceId, sharedWithUsers, charts, graphViewId? }
 * @param {object} options - { userId, tenantId, headers }
 * @returns {Promise} API response data
 */
export const saveGraphView = async (payload, options = {}) => {
	const isUpdate = !!payload.graphViewId;
	const res = await axios({
		method: isUpdate ? "PUT" : "POST",
		url: `${BACKEND_API}/graph-view`,
		data: payload,
		headers: authHeaders(options),
	});
	return res.data;
};

/**
 * List saved graph views (optionally scoped to a space).
 * GET /graph-view   |   GET /graph-view?spaceId=..
 * @param {string} [spaceId]
 * @param {object} options - { userId, tenantId, headers }
 * @returns {Promise} API response data
 */
export const getGraphViews = async (spaceId, options = {}) => {
	const res = await axios({
		method: "GET",
		url: spaceId ? `${BACKEND_API}/graph-view?spaceId=${spaceId}` : `${BACKEND_API}/graph-view`,
		headers: authHeaders(options),
	});
	return res.data;
};

/**
 * Get a single saved graph view by id.
 * GET /graph-view/{graphViewId}
 * @param {string} graphViewId
 * @param {object} options - { userId, tenantId, headers }
 * @returns {Promise} API response data
 */
export const getGraphView = async (graphViewId, options = {}) => {
	const res = await axios({
		method: "GET",
		url: `${BACKEND_API}/graph-view/${graphViewId}`,
		headers: authHeaders(options),
	});
	return res.data;
};

/**
 * Move the user's default graph view from the current default to a new one.
 * PUT /graph/default-viewers
 * @param {object} payload - { userId, sourceGraphId, destinationGraphId }
 *   sourceGraphId: the user's current default dashboard id ("" if none)
 *   destinationGraphId: the dashboard to make default
 * @param {object} options - { userId, tenantId, headers }
 * @returns {Promise} API response data
 */
export const setGraphViewDefault = async (payload, options = {}) => {
	const tenantId = options.tenantId || localStorage.getItem("tenantId") || 1;
	const res = await axios({
		method: "PUT",
		url: `${BACKEND_API}/graph/default-viewers`,
		data: { ...payload, tenantId },
		headers: authHeaders(options),
	});
	return res.data;
};

/**
 * Delete a saved graph view.
 * DELETE /graph-view/{graphViewId}
 * @param {string} graphViewId
 * @param {object} options - { userId, tenantId, headers }
 * @returns {Promise} API response data
 */
export const deleteGraphView = async (graphViewId, options = {}) => {
	const res = await axios({
		method: "DELETE",
		url: `${BACKEND_API}/graph-view/${graphViewId}`,
		headers: authHeaders(options),
	});
	return res.data;
};
